import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
    getApiCaller,
    postApiCaller,
    putApiCaller,
    deleteApiCaller
} from "../../Libs/apiCaller";

const initialState = {
    tasks: [],
    isLoading: false,
    error: null,
    isDrawerOpen: false,
    activeFilter: "all", // 'all', 'me', 'completed'
};

export const fetchTasks = createAsyncThunk(
    "task/fetchTasks",
    async (conversationId, { rejectWithValue }) => {
        try {
            // If conversationId is null/undefined/'me', we pass 'me' to the backend 
            // OR we can rely on how standard REST APIs work. 
            // In TaskController, we updated logic to handle 'me' string or undefined.
            const targetId = conversationId || 'me';
            const response = await getApiCaller(`/tasks/${targetId}`);
            if (response.status === 200) {
                return response.data;
            }
            return rejectWithValue(response.response?.data?.message || "Error fetching tasks");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error fetching tasks");
        }
    }
);

export const createTask = createAsyncThunk(
    "task/createTask",
    async (taskData, { rejectWithValue }) => {
        try {
            const response = await postApiCaller("/tasks", taskData);
            if (response.status === 201) {
                return response.data;
            }
            return rejectWithValue(response.response?.data?.message || "Error creating task");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error creating task");
        }
    }
);

export const updateTask = createAsyncThunk(
    "task/updateTask",
    async ({ taskId, updates }, { rejectWithValue }) => {
        try {
            const response = await putApiCaller(`/tasks/${taskId}`, updates);
            if (response.status === 200) {
                return response.data;
            }
            return rejectWithValue(response.response?.data?.message || "Error updating task");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error updating task");
        }
    }
);

export const deleteTask = createAsyncThunk(
    "task/deleteTask",
    async (taskId, { rejectWithValue }) => {
        try {
            const response = await deleteApiCaller(`/tasks/${taskId}`);
            if (response.status === 200) {
                return taskId;
            }
            return rejectWithValue(response.response?.data?.message || "Error deleting task");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error deleting task");
        }
    }
);

// Bulk reorder
export const reorderTasks = createAsyncThunk(
    "task/reorderTasks",
    async (tasks, { rejectWithValue }) => {
        try {
            // Optimistically update UI first (handled in extraReducers if needed, 
            // but usually we update state immediately in dragEnd handler and then sync)
            const response = await putApiCaller("/tasks/reorder/bulk", { tasks });
            if (response.status === 200) {
                return tasks;
            }
            return rejectWithValue(response.response?.data?.message || "Error reordering tasks");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error reordering tasks");
        }
    }
);

export const addComment = createAsyncThunk(
    "task/addComment",
    async ({ taskId, text }, { rejectWithValue }) => {
        try {
            const response = await postApiCaller(`/tasks/${taskId}/comments`, { text });
            if (response.status === 200) {
                return response.data;
            }
            return rejectWithValue(response.response?.data?.message || "Error adding comment");
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || "Error adding comment");
        }
    }
);

const taskSlice = createSlice({
    name: "task",
    initialState,
    reducers: {
        toggleDrawer: (state, action) => {
            state.isDrawerOpen = action.payload ?? !state.isDrawerOpen;
        },
        setFilter: (state, action) => {
            state.activeFilter = action.payload;
        },
        // For socket updates
        updateTaskRealtime: (state, action) => {
            const updatedTask = action.payload;
            const index = state.tasks.findIndex(t => t._id === updatedTask._id);
            if (index !== -1) {
                state.tasks[index] = updatedTask;
            } else {
                state.tasks.push(updatedTask);
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Tasks
            .addCase(fetchTasks.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchTasks.fulfilled, (state, action) => {
                state.isLoading = false;
                state.tasks = action.payload;
            })
            .addCase(fetchTasks.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            // Create Task
            .addCase(createTask.fulfilled, (state, action) => {
                state.tasks.push(action.payload);
            })
            // Update Task
            .addCase(updateTask.fulfilled, (state, action) => {
                const index = state.tasks.findIndex((t) => t._id === action.payload._id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                }
            })
            // Delete Task
            .addCase(deleteTask.fulfilled, (state, action) => {
                state.tasks = state.tasks.filter((t) => t._id !== action.payload);
            })
            // Add Comment - typically returns the updated task
            .addCase(addComment.fulfilled, (state, action) => {
                const index = state.tasks.findIndex((t) => t._id === action.payload._id);
                if (index !== -1) {
                    state.tasks[index] = action.payload;
                }
            });
    },
});

export const { toggleDrawer, setFilter, updateTaskRealtime } = taskSlice.actions;
export default taskSlice.reducer;
